import { ApiError } from "../api/client";

/**
 * Utilidades puras del segundo factor (MFA con app, códigos de recuperación y
 * PIN de conductor). Sin React ni red, para poder probarlas con vitest.
 */

export type SecondFactorKind = "totp" | "pin";

export interface SecondFactorRequest {
  kind: SecondFactorKind;
  /** El PIN temporal que se tecleó ha caducado: hay que pedir uno nuevo al administrador. */
  expired: boolean;
}

/**
 * La API avisa de que falta (o es incorrecto) el segundo factor con
 * `error.details.secondFactor`. Se lee ese campo en lugar de buscar palabras
 * en el mensaje, que puede cambiar de texto.
 */
export function secondFactorFromError(error: unknown): SecondFactorRequest | null {
  if (!(error instanceof ApiError)) return null;
  const details = error.details;
  if (typeof details !== "object" || details === null) return null;
  const { secondFactor, expired } = details as { secondFactor?: unknown; expired?: unknown };
  if (secondFactor !== "totp" && secondFactor !== "pin") return null;
  return { kind: secondFactor, expired: expired === true };
}

/** Código de la app (6 dígitos) o de recuperación (XXXXX-XXXXX): se limpia lo que se pega. */
export function cleanSecondFactorInput(raw: string): string {
  return raw.trim().replace(/\s+/g, "");
}

/** ¿Tiene forma de código de recuperación y no de TOTP de 6 dígitos? */
export function looksLikeRecoveryCode(raw: string): boolean {
  return /[A-Za-z]/.test(raw) || raw.replace(/[^0-9A-Za-z]/g, "").length > 6;
}

/** Texto que se copia o se descarga con los códigos de recuperación. */
export function recoveryCodesText(codes: string[], appName: string, email: string): string {
  return [
    `Códigos de recuperación de ${appName}`,
    `Cuenta: ${email}`,
    "",
    "Cada código vale una sola vez. Guárdalos en un lugar seguro (gestor de contraseñas).",
    "",
    ...codes,
    "",
  ].join("\n");
}

/** Mensaje claro cuando la API responde 429 por demasiados intentos. */
export function lockoutMessage(error: unknown): string | null {
  if (error instanceof ApiError && error.status === 429) return error.message;
  return null;
}
